import {
  AnalyticsChartData,
  ProjectChartType,
} from "@custom-types/analytics-types";
import { ProjectId } from "@custom-types/base-types";
import { SdbProject } from "@custom-types/project-types";
import { createSelector } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { AnalyticsState } from "@store/analytics/analytics-slice";
import { selectedProjectIdSelector } from "@store/projects/projects-selector";
import { RootState } from "@store/store-helper";
import { getChartsData } from "@utils/analytics-utils";
import { TimeFrameItem } from "@utils/time-utils";

/**
 * Returns the selected project.
 */
export const selectedAnalyticsProjectSelector: (
  state: RootState
) => SdbProject | null = createSelector(
  (state: RootState) => state.analytics.selectedAnalyticsProjectId,
  (state: RootState) => state.projects.entities,
  (selectedProjectId, projectEntities) => {
    if (!selectedProjectId) {
      return null;
    }
    return projectEntities[selectedProjectId] ?? null;
  }
);

/**
 * Returns the selected time frame.
 */
export const selectedAnalyticsTimeFrameSelector: (
  state: RootState
) => RootState["analytics"]["selectedAnalyticsTimeFrame"] = createSelector(
  (state: RootState) => state.analytics.selectedAnalyticsTimeFrame,
  (selectedTimeFrame) => {
    return selectedTimeFrame;
  }
);

/** Returns the company stats */
export const companyStatsSelector: (
  state: RootState
) => FaroCoreApiTypes.CompanyStatistic[] | null = createSelector(
  (state: RootState) => state.analytics.companyStats,
  (companyStats) => {
    return companyStats;
  }
);

/**
 * Returns true if the current stats are company level (all projects)
 * or false if the current stats are project level
 */
export const isCompanyStatsSelector: (state: RootState) => boolean =
  createSelector(selectedAnalyticsProjectSelector, (selectedProject) => {
    return selectedProject === null;
  });

/**
 * Returns the stats for the selected project
 */
export const selectedProjectStatsSelector: (
  state: RootState
) => FaroCoreApiTypes.CompanyStatistic[] | null = createSelector(
  (state: RootState) => state.analytics.selectedAnalyticsProjectId,
  (state: RootState) => state.analytics.projectStats,
  (projectId, projectStats) => {
    if (!projectId) {
      return null;
    }
    return projectStats[projectId] ?? null;
  }
);

/** Returns the charts data that will be rendered as charts*/
export const chartsDataSelector: (state: RootState) => AnalyticsChartData[] =
  createSelector(
    selectedAnalyticsTimeFrameSelector,
    (state: RootState) => state.analytics.selectedAnalyticsProjectId,
    companyStatsSelector,
    selectedProjectStatsSelector,
    (
      timeFrame,
      selectedAnalyticsProjectId,
      companyStats,
      selectedProjectStats
    ) => {
      let stats: FaroCoreApiTypes.CompanyStatistic[] | null;

      // If selected project ID is null then use the company stats
      // If selected project ID is defined then use the selected project stats
      if (selectedAnalyticsProjectId === null) {
        stats = companyStats;
      } else {
        stats = selectedProjectStats;
      }

      return getChartsData({
        timeFrame,
        stats,
      });
    }
  );

/**
 * Returns the stats for the provided project
 */
export function projectStatsSelector(
  projectId: ProjectId
): (state: RootState) => FaroCoreApiTypes.CompanyStatistic[] | null {
  return createSelector(
    (state: RootState) => state.analytics.projectStats[projectId],
    (projectStats) => {
      return projectStats ?? null;
    }
  );
}

/**
 * Returns the fetching properties of the analytics slice.
 */
export const fetchingAnalyticsFlagsSelector: (
  state: RootState
) => AnalyticsState["fetching"] = createSelector(
  (state: RootState) => state.analytics.fetching,
  (fetching) => {
    return fetching;
  }
);

/**
 * Returns the selected project chart type
 */
export const selectedProjectChartTimeFrameSelector: (
  state: RootState
) => TimeFrameItem["value"] = createSelector(
  (state: RootState) => state.analytics.projectOverview.selectedTimeFrame,
  (selectedTimeFrame) => {
    return selectedTimeFrame;
  }
);

/**
 * Returns the selected project chart type
 */
export const selectedProjectChartTypeSelector: (
  state: RootState
) => ProjectChartType = createSelector(
  (state: RootState) => state.analytics.projectOverview.selectedChartType,
  (selectedChartType) => {
    return selectedChartType;
  }
);

/**
 * Returns the selected chart for the current selected project
 */
export const projectChartSelector: (
  state: RootState
) => AnalyticsChartData | undefined = createSelector(
  selectedProjectIdSelector,
  (state: RootState) => state.analytics.projectStats,
  selectedProjectChartTimeFrameSelector,
  selectedProjectChartTypeSelector,
  (projectId, projectStats, timeFrame, chartType) => {
    if (!projectId) {
      return undefined;
    }

    const stats = projectStats[projectId] ?? null;
    const charts = getChartsData({ timeFrame, stats });
    const selectedChart = charts.find((chart) => chart.name === chartType);

    return selectedChart;
  }
);
